import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  useToast,
} from "@asm/ui";
import { AlertTriangle, ShieldAlert, Shield, Info, Search, X, ChevronDown, ChevronLeft, ChevronRight, Calendar, Download, Loader2 } from "lucide-react";
import { adminApi, ReconFinding, ReconCampaign } from "@/lib/api";

const severityConfig = {
  critical: {
    icon: ShieldAlert,
    color: "bg-red-500 text-white",
    label: "Critical",
  },
  high: {
    icon: AlertTriangle,
    color: "bg-orange-500 text-white",
    label: "High",
  },
  medium: {
    icon: Shield,
    color: "bg-yellow-500 text-black",
    label: "Medium",
  },
  low: {
    icon: Info,
    color: "bg-blue-500 text-white",
    label: "Low",
  },
};

const MODULE_LABELS: Record<string, string> = {
  dns: "DNS RECORDS",
  email: "EMAIL SECURITY",
  tls: "TLS / SSL",
  headers: "SECURITY HEADERS",
  whois: "DOMAIN WHOIS",
  tech: "TECHNOLOGIES",
  cloud: "CLOUD ASSETS",
  cloud_misconfig: "CLOUD MISCONFIGS",
  asn: "NETWORK INTEL",
  repos: "CODE REPOS",
  crawl_hints: "CRAWL ANALYSIS",
  js_analysis: "JS ANALYSIS",
  favicon: "FAVICON ANALYSIS",
  subdomains: "SUBDOMAINS",
  portscan: "PORT SCAN",
  ipintel: "IP INTELLIGENCE",
  typosquat: "TYPOSQUATTING",
  bruteforce: "DNS BRUTEFORCE",
  service_probe: "SERVICE PROBE",
  vuln: "VULNERABILITIES",
};

function formatModuleName(raw: string): string {
  return MODULE_LABELS[raw] || raw.replace(/_/g, " ").toUpperCase();
}

/** Searchable filter — combobox that combines text input with a suggestion dropdown. */
function SearchableFilter({
  placeholder,
  options,
  value,
  onChange,
  formatLabel,
  className = "",
}: {
  placeholder: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  formatLabel?: (v: string) => string;
  className?: string;
}) {
  const label = formatLabel || ((v: string) => v);
  const [inputText, setInputText] = useState(value === "all" ? "" : label(value));
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync external value changes (e.g. clearFilters)
  useEffect(() => {
    setInputText(value === "all" ? "" : label(value));
  }, [value, label]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = useMemo(() => {
    if (!inputText) return options;
    const q = inputText.toLowerCase();
    return options.filter(
      (o) => o.toLowerCase().includes(q) || label(o).toLowerCase().includes(q)
    );
  }, [options, inputText, label]);

  const handleInput = useCallback(
    (text: string) => {
      setInputText(text);
      setOpen(true);
      // Apply as substring filter immediately (not exact match)
      onChange(text || "all");
    },
    [onChange]
  );

  const handleSelect = useCallback(
    (option: string) => {
      setInputText(label(option));
      onChange(option);
      setOpen(false);
    },
    [onChange, label]
  );

  const handleClear = useCallback(() => {
    setInputText("");
    onChange("all");
    setOpen(false);
  }, [onChange]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Input
        placeholder={placeholder}
        value={inputText}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => setOpen(true)}
        className="pr-14"
      />
      <div className="absolute right-1 top-1 flex items-center gap-0.5">
        {inputText && (
          <button
            onClick={handleClear}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => setOpen(!open)}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-md border bg-popover shadow-md">
          <button
            onClick={() => { handleClear(); }}
            className="w-full px-3 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
          >
            {placeholder}
          </button>
          {filtered.map((option) => (
            <button
              key={option}
              onClick={() => handleSelect(option)}
              className={`w-full px-3 py-1.5 text-left text-sm hover:bg-accent ${
                option === value ? "bg-accent font-medium" : ""
              }`}
            >
              {label(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type DatePreset = "all" | "24h" | "7d" | "30d" | "90d" | "custom";

interface DateRange {
  preset: DatePreset;
  from: string; // YYYY-MM-DD or ""
  to: string;   // YYYY-MM-DD or ""
}

const DATE_PRESETS: { value: DatePreset; label: string; days?: number }[] = [
  { value: "all", label: "Any time" },
  { value: "24h", label: "24h", days: 1 },
  { value: "7d", label: "7d", days: 7 },
  { value: "30d", label: "30d", days: 30 },
  { value: "90d", label: "90d", days: 90 },
  { value: "custom", label: "Range" },
];

/** Date range filter — quick presets + expandable custom from/to range. */
function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const activeLabel = (() => {
    if (value.preset === "all") return "Any time";
    if (value.preset === "custom") {
      if (value.from && value.to) return `${value.from} — ${value.to}`;
      if (value.from) return `From ${value.from}`;
      if (value.to) return `Until ${value.to}`;
      return "Custom range";
    }
    return `Last ${DATE_PRESETS.find((p) => p.value === value.preset)?.label}`;
  })();

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border bg-transparent text-sm hover:bg-accent transition-colors"
      >
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={value.preset === "all" ? "text-muted-foreground" : ""}>{activeLabel}</span>
        {value.preset !== "all" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange({ preset: "all", from: "", to: "" });
            }}
            className="ml-0.5 p-0.5 rounded hover:bg-muted"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 rounded-md border bg-popover shadow-md p-3 space-y-3">
          {/* Preset chips */}
          <div className="flex flex-wrap gap-1.5">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => {
                  if (preset.value === "custom") {
                    onChange({ ...value, preset: "custom" });
                  } else {
                    onChange({ preset: preset.value, from: "", to: "" });
                    setOpen(false);
                  }
                }}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  value.preset === preset.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-accent"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom date range inputs */}
          {value.preset === "custom" && (
            <div className="space-y-2 pt-1 border-t">
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground w-10">From</label>
                <Input
                  type="date"
                  value={value.from}
                  onChange={(e) => onChange({ ...value, from: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground w-10">To</label>
                <Input
                  type="date"
                  value={value.to}
                  onChange={(e) => onChange({ ...value, to: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Finding extended with campaign info
interface FindingWithCampaign extends ReconFinding {
  campaign_name?: string;
}

export function ReconFindings() {
  const { toast } = useToast();
  const [findings, setFindings] = useState<FindingWithCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  // Campaign state
  const [campaigns, setCampaigns] = useState<ReconCampaign[]>([]);
  const [campaignFilter, setCampaignFilter] = useState<string>("all");

  // Filter state (server-side)
  const [searchText, setSearchText] = useState("");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [domainFilter, setDomainFilter] = useState<string>("all");
  const [moduleFilter, setModuleFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange>({ preset: "all", from: "", to: "" });

  // Debounced search to avoid spamming the API
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Server-side pagination & counts
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [highCount, setHighCount] = useState(0);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [severityFilter, domainFilter, moduleFilter, debouncedSearch, dateRange, campaignFilter]);

  // Fetch campaigns list once
  useEffect(() => {
    adminApi.listCampaigns({ per_page: 100 }).then((res) => {
      setCampaigns(res.campaigns);
    }).catch(() => {});
  }, []);

  // Fetch findings — server-side paginated and filtered
  useEffect(() => {
    const fetchFindings = async () => {
      try {
        setLoading(true);
        const campaign = campaignFilter !== "all" ? campaigns.find((c) => c.id === campaignFilter) : undefined;
        const result = await adminApi.getHighSeverityFindings({
          page,
          per_page: perPage,
          severity: severityFilter !== "all" ? severityFilter : undefined,
          domain: domainFilter !== "all" ? domainFilter : undefined,
          module: moduleFilter !== "all" ? moduleFilter : undefined,
          search: debouncedSearch || undefined,
          campaign_id: campaignFilter !== "all" ? campaignFilter : undefined,
        });
        setFindings(result.findings.map((f) => ({ ...f, campaign_name: campaign?.name })));
        setTotal(result.total);
        setCriticalCount(result.critical_count);
        setHighCount(result.high_count);
      } catch (err) {
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to load findings",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFindings();
  }, [toast, campaignFilter, campaigns, page, perPage, severityFilter, domainFilter, moduleFilter, debouncedSearch, dateRange]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Autocomplete suggestions from current page
  const uniqueDomains = useMemo(
    () => [...new Set(findings.map((f) => f.domain))].sort(),
    [findings]
  );
  const uniqueModules = useMemo(
    () => [...new Set(findings.map((f) => f.module))].sort(),
    [findings]
  );

  // Server-side pagination
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const hasActiveFilters =
    searchText || severityFilter !== "all" || domainFilter !== "all" || moduleFilter !== "all" || dateRange.preset !== "all" || campaignFilter !== "all";

  const clearFilters = () => {
    setSearchText("");
    setSeverityFilter("all");
    setDomainFilter("all");
    setModuleFilter("all");
    setDateRange({ preset: "all", from: "", to: "" });
    setCampaignFilter("all");
  };

  // Export state
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    try {
      setExporting(true);
      await adminApi.exportHighSeverityFindings({
        severity: severityFilter !== "all" ? severityFilter : undefined,
        domain: domainFilter !== "all" ? domainFilter : undefined,
        module: moduleFilter !== "all" ? moduleFilter : undefined,
        search: debouncedSearch || undefined,
        campaign_id: campaignFilter !== "all" ? campaignFilter : undefined,
      });
      toast({ title: "Export complete", description: `${total.toLocaleString()} findings exported` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Failed to export findings",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-red-500/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Findings</CardTitle>
            <ShieldAlert className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{criticalCount}</div>
            <p className="text-xs text-muted-foreground">
              Require immediate attention
            </p>
          </CardContent>
        </Card>

        <Card className="border-orange-500/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Severity</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{highCount}</div>
            <p className="text-xs text-muted-foreground">
              Should be addressed soon
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3">
            {/* Row 1: Text search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search findings by title, description, or domain..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Row 2: Filter dropdowns */}
            <div className="flex flex-wrap gap-3">
              <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Campaign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              <SearchableFilter
                placeholder="All Domains"
                options={uniqueDomains}
                value={domainFilter}
                onChange={setDomainFilter}
                className="w-[220px]"
              />

              <SearchableFilter
                placeholder="All Modules"
                options={uniqueModules}
                value={moduleFilter}
                onChange={setModuleFilter}
                formatLabel={formatModuleName}
                className="w-[200px]"
              />

              <DateRangeFilter value={dateRange} onChange={setDateRange} />

              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting || total === 0}
                className="h-9 gap-1.5"
                title={`Export ${total.toLocaleString()} findings as .json.gz`}
              >
                {exporting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export{total > 0 ? ` (${total.toLocaleString()})` : ""}
              </Button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear filters
                </button>
              )}
            </div>

            {/* Active filter count */}
            {hasActiveFilters && (
              <p className="text-sm text-muted-foreground">
                {total.toLocaleString()} findings match filters
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Findings Table */}
      <Card>
        <CardHeader>
          <CardTitle>High Severity Findings</CardTitle>
          <CardDescription>
            Critical and high severity security issues found during reconnaissance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : findings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {hasActiveFilters ? (
                <>
                  <p>No findings match your filters.</p>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary hover:underline mt-1"
                  >
                    Clear all filters
                  </button>
                </>
              ) : (
                <>
                  <p>No high severity findings detected.</p>
                  <p className="text-sm">All scanned domains are looking good!</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Severity</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Finding</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Found</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {findings.map((finding, index) => {
                      const config = severityConfig[finding.severity] || severityConfig.low;
                      const Icon = config.icon;

                      return (
                        <TableRow key={`${finding.result_id}-${index}`}>
                          <TableCell>
                            <Badge className={config.color}>
                              <Icon className="h-3 w-3 mr-1" />
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {finding.campaign_name ? (
                              <span className="text-foreground">{finding.campaign_name}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {finding.domain}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{finding.title}</div>
                              <div className="text-sm text-muted-foreground">
                                {finding.description}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{formatModuleName(finding.module)}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(finding.found_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    {total.toLocaleString()} findings — page {page} of {totalPages}
                  </span>
                  <span className="text-border">|</span>
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="h-8 rounded-md border bg-transparent px-2 text-sm"
                  >
                    {[25, 50, 100, 200].map((n) => (
                      <option key={n} value={n}>
                        {n} / page
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || loading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
